import moment from 'moment';
import {NotificationManager} from '../components/common/react-notifications';
import {
  defaultDirection,
  defaultLocale,
  defaultColor,
  localeOptions,
  themeColorStorageKey,
  themeRadiusStorageKey,
} from '../constants/defaultValues';

/**
 * @param {number} seconds - Duration in seconds
 * @returns {string} Human-readable time
 */
export const convertSecondsToReadable = seconds => {
  // null for invalid entries
  if (seconds < 0 || Number.isNaN(seconds)) return null;
  if (seconds === 0) return '0 min';
  if (seconds < 60) return '< 1 min';

  let duration = seconds;
  const remainder = duration % 60;
  duration = (duration - remainder) / 60;
  const minutes = duration % 60;
  duration = (duration - minutes) / 60;
  const hours = duration % 24;
  const days = (duration - hours) / 24;

  const daysText = days ? `${days} day${days > 1 ? 's' : ''}` : '';
  const hoursText = hours ? `${hours} hr${hours > 1 ? 's' : ''}` : '';
  const minutesText = minutes ? `${minutes} min` : '';

  return [daysText, hoursText, minutesText].filter(val => val).join(' ');
};

// Converts seconds into mm:ss
// e.g. 300 to 05:00
export const convertSecondsToMMSS = seconds => {
  let displayString = '';
  const secondsAfterMinutes = seconds % 60;
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const paddedMinutes = minutes.toString().padStart(2, 0);
    displayString += paddedMinutes;
  } else {
    displayString += '00';
  }
  const paddedSeconds = secondsAfterMinutes.toString().padStart(2, 0);
  displayString += `:${paddedSeconds}`;
  return displayString;
};

export const mapOrder = (array, order, key) => {
  array.sort((a, b) => {
    const A = a[key];
    const B = b[key];
    if (order.indexOf(`${A}`) > order.indexOf(`${B}`)) {
      return 1;
    }
    return -1;
  });
  return array;
};

export const getDateWithFormat = () => {
  const today = new Date();
  let dd = today.getDate();
  let mm = today.getMonth() + 1; // January is 0!

  const yyyy = today.getFullYear();
  if (dd < 10) {
    dd = `0${dd}`;
  }
  if (mm < 10) {
    mm = `0${mm}`;
  }
  return `${dd}.${mm}.${yyyy}`;
};

export const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours()}:${now.getMinutes()}`;
};

/**
 * @param {number} energy - Energy in Wh (no manipulation from db)
 * @returns {string} Distance in miles, formatted to 1 digit after decimal point
 */
export const computeEstimatedDistance = energy => {
  // Estimation 100 miles/30kWh from Portals
  const efficiency = 100 / 30000;
  return (energy * efficiency).toFixed(1);
};

/**
 * @param {number} energy - Energy in Wh (no manipulation from db)
 * @returns {obj} Energy formatted to 3 digit after decimal point and appropriate units
 */
export const getFormattedEnergy = energy => {
  const [divisor, units] = energy > 999999
    ? [1000000, 'MWh']
    : [1000, 'kWh'];

  return {
    formattedEnergy: (energy / divisor).toFixed(3),
    units,
  };
};

/**
 * @param {number} cost - Cost/uom for the session
 * @param {string} uom - unit of measurement for the cost
 * @param {number} energy - Amount of energy charged in Wh
 * @param {number} start - Start of charge in Unix ms
 * @returns {number} - Estimated cost in dollars
 */
export const computeCostEstimate = ({
  cost, uom, energy, start,
}) => {
  if (uom === '/kWh') {
    return (cost * energy) / 1000;
  }
  const now = Date.now() / 1000;
  return cost * ((now - start) / (60 * 60));
};

/**
 *
 * @param {number} cents - monetary amount in cents
 * @returns {string} $-prefixed dollar amount,
 * with comma thousand separators and 2 post-decimal digits
 */
export const centsToDollarString = cents => Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'USD',
}).format(cents / 100);

/**
 * Checks if the given DR Event has ended
 * @param {object} drEvent - DR Event
 * @returns {boolean} True if DR event time has passed and false otherwise
 */
export const checkDrEventEnded = drEvent => {
  // No DR Event so just assume it's over
  if (!drEvent?.startTime) return true;
  const now = Date.now();
  // Over if now is past given end time
  if (drEvent.endTime) return now / 1000 > drEvent.endTime;
  const drEventDate = new Date(drEvent.startTime * 1000);
  // set to 9pm since that's what we show users for this case
  drEventDate.setHours(21, 0, 0, 0);
  // Over if now is past 9pm on whatever day
  return now > drEventDate;
};

/**
 * Formats DR Event start/end time
 * @param {object} drEvent
 * @param {number} [drEvent.startTime] - start of dr event
 * @param {number} [drEvent.endTime] - end of dr event
 * @param {number} [drEvent.percentReduction] - Percent reduction of charge
 * @returns {object} - DR Event with start and end time formatted to be human-readable strings
 */
export const formatDREventTime = drEvent => {
  if (!drEvent?.startTime) return drEvent;
  let formatter = 'dddd, MMMM D';
  if (drEvent.percentReduction) formatter += ' [at] h:mm A';
  const rtn = {
    ...drEvent,
    startTime: moment.unix(drEvent.startTime).format(formatter),
  };
  if (rtn.endTime) rtn.endTime = moment.unix(rtn.endTime).format(formatter);
  return rtn;
};

/**
 * Formats site payment for UI components
 * @param {object} site - Site object from Portals
 * @returns {object} site object with formatted payment info
 */
export const formatSitePayment = site => {
  const siteInfo = {};

  if (!site.paymentRequired) return siteInfo;

  // convert to cost from cents to dollars if payment site
  // OK to manipulate this here because Stations have these values as well
  const {costPerKwh, costPerHour} = site;
  siteInfo.cost = centsToDollarString(costPerKwh || costPerHour);
  siteInfo.uom = costPerHour ? 'hr' : 'kWh';

  return siteInfo;
};

/**
 * Formats response from Portals API to only include values the app uses
 */
export const formatUserStateInfo = user => ({
  activated: user.activated,
  billingId: user.billingId,
  canModifyPayment: user.canModifyPayment,
  card: user.card,
  cellPhone: user.cellPhone,
  currChargingStation: user.currChargingStation,
  customerId: user.customerId,
  firstName: user.firstName,
  fullName: user.fullName,
  id: user.id,
  lastName: user.lastName,
  pastDueObject: user.pastDueObject,
  receiptPreference: user.receiptPreference,
  roleId: user.roleId,
  transactionType: user.transactionType,
  username: user.username,
  workPhone: user.workPhone,
});

/* ------------ LOCAL STORAGE ------------ */

export const getDirection = () => {
  let direction = defaultDirection;

  try {
    if (localStorage.getItem('direction')) {
      const localValue = localStorage.getItem('direction');
      if (localValue === 'rtl' || localValue === 'ltr') {
        direction = localValue;
      }
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : getDirection -> error', error);
    direction = defaultDirection;
  }
  return {
    direction,
    isRtl: direction === 'rtl',
  };
};
export const setDirection = localValue => {
  let direction = 'ltr';
  if (localValue === 'rtl' || localValue === 'ltr') {
    direction = localValue;
  }
  try {
    localStorage.setItem('direction', direction);
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setDirection -> error', error);
  }
};

export const getCurrentColor = () => {
  let currentColor = defaultColor;
  try {
    if (localStorage.getItem(themeColorStorageKey)) {
      currentColor = localStorage.getItem(themeColorStorageKey);
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : getCurrentColor -> error', error);
    currentColor = defaultColor;
  }
  return currentColor;
};

export const setCurrentColor = color => {
  try {
    localStorage.setItem(themeColorStorageKey, color);
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setCurrentColor -> error', error);
  }
};

export const getCurrentRadius = () => {
  let currentRadius = 'rounded';
  try {
    if (localStorage.getItem(themeRadiusStorageKey)) {
      currentRadius = localStorage.getItem(themeRadiusStorageKey);
    }
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : getCurrentRadius -> error',
      error,
    );
    currentRadius = 'rounded';
  }
  return currentRadius;
};
export const setCurrentRadius = radius => {
  try {
    localStorage.setItem(themeRadiusStorageKey, radius);
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : setCurrentRadius -> error',
      error,
    );
  }
};

export const getCurrentLanguage = () => {
  let language = defaultLocale;
  try {
    const localStorageLanguage = localStorage.getItem('currentLanguage');
    language = localStorageLanguage
      && localeOptions.filter(
        option => option.id === localStorageLanguage,
      ).length > 0
      ? localStorageLanguage
      : defaultLocale;
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : getCurrentLanguage -> error',
      error,
    );
    language = defaultLocale;
  }
  return language;
};
export const setCurrentLanguage = locale => {
  try {
    localStorage.setItem('currentLanguage', locale);
  } catch (error) {
    console.log(
      '>>>>: src/helpers/Utils.js : setCurrentLanguage -> error',
      error,
    );
  }
};

export const getCurrentUser = () => {
  let user = null;
  try {
    user = localStorage.getItem('current_user') !== null
      ? JSON.parse(localStorage.getItem('current_user'))
      : null;
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js  : getCurrentUser -> error', error);
    user = null;
  }
  return user;
};

export const setCurrentUser = user => {
  try {
    if (user) {
      localStorage.setItem('current_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('current_user');
    }
  } catch (error) {
    console.log('>>>>: src/helpers/Utils.js : setCurrentUser -> error', error);
  }
};

export const checkUserLoggedIn = () => !!getCurrentUser()?.id;

export const removeCurrentUser = () => {
  localStorage.removeItem('current_user');
};

// Creates Axios Headers for API calls
export const getOptions = () => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    },
  };
  return options;
};

export const setBannerDismissed = () => {
  localStorage.setItem('hideBanner', true);
};

export const resetBanner = () => {
  localStorage.removeItem('hideBanner');
};

export const checkBannerDismissed = () => localStorage.getItem('hideBanner');

export const formatPhoneNumber = value => {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) {
    return value;
  }

  // clean the input for any non-digit values.
  const phoneNumber = value.replace(/[^\d]/g, '');

  // phoneNumberLength is used to know when to apply our formatting for the phone number
  const phoneNumberLength = phoneNumber.length;

  // we need to return the value with no formatting if its less then four digits
  // this is to avoid weird behavior that occurs if you  format the area code to early

  if (phoneNumberLength < 4) return phoneNumber;

  // if phoneNumberLength is greater than 4 and less the 7 we start to return
  // the formatted number
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  // finally, if the phoneNumberLength is greater then seven, we add the last
  // bit of formatting and return it.
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6,
  )}-${phoneNumber.slice(6, 10)}`;
};

export const formatZipCode = zipCode => {
  if (!zipCode) return '';

  // clean the input for any non-digit values.
  const cleanedZipCode = zipCode.replace(/[^\d]/g, '');

  return cleanedZipCode.toString().slice(0, 5);
};
